<script lang="ts">
  import type { Project, ProjectCategory } from '../lib/types';
  import { categoryIcons, statusAccentColors } from '../lib/utils';
  import {
    isWorkQueueOpen,
    workQueueTab,
    closeWorkQueue,
    draggedProject,
    openProjectModalForCreate,
    requestProjectMove,
    openProjectRail,
    openProjectScheduleModal,
  } from '../lib/stores/ui';
  import WorkQueueTabs from './WorkQueueTabs.svelte';
  import ActionSheet from './mobile/ActionSheet.svelte';
  import Icon from './Icon.svelte';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    projects: Project[];
  }

  let { projects }: Props = $props();

  let searchQuery = $state('');
  let selectedCategory = $state<ProjectCategory | null>(null);
  let isDropTarget = $state(false);

  const categories: { value: ProjectCategory; icon: string; label: string }[] = [
    { value: 'tech', icon: '💻', label: 'Tech' },
    { value: 'health', icon: '🏃', label: 'Health' },
    { value: 'personal', icon: '👤', label: 'Personal' },
    { value: 'finance', icon: '💰', label: 'Finance' },
    { value: 'travel', icon: '✈️', label: 'Travel' },
    { value: 'other', icon: '📋', label: 'Other' },
  ];

  // Sort: starred first, then alphabetical. Then apply filters.
  let filteredProjects = $derived.by(() => {
    let result = [...projects];

    // Starred/favorites first
    result.sort((a, b) => {
      if (a.isPriority && !b.isPriority) return -1;
      if (!a.isPriority && b.isPriority) return 1;
      return a.title.localeCompare(b.title);
    });

    // Filter by category
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.prefix?.toLowerCase().includes(query) ||
          p.notes?.toLowerCase().includes(query),
      );
    }

    return result;
  });

  let isFiltered = $derived(!!searchQuery.trim() || !!selectedCategory);

  function toggleCategory(cat: ProjectCategory) {
    if (selectedCategory === cat) {
      selectedCategory = null;
    } else {
      selectedCategory = cat;
    }
  }

  function clearFilters() {
    searchQuery = '';
    selectedCategory = null;
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if ($draggedProject) {
      isDropTarget = true;
    }
  }

  function handleDragLeave() {
    isDropTarget = false;
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDropTarget = false;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (!projectId) return;

    // Don't move if it's already in backlog — no request, no toast
    if ($draggedProject.schedulingState === 'backlog') return;

    requestProjectMove(projectId, $draggedProject, 'backlog', {});
  }

  function handleAddWithModal() {
    openProjectModalForCreate({
      schedulingState: 'backlog',
    });
  }

  // Mobile: tapping a project offers Schedule… (the tap path for drag-to-months)
  // and Open project, in an action sheet over the work queue.
  let actionProject = $state<Project | null>(null);
  let actionsOpen = $state(false);

  function handleChipClick(project: Project) {
    hideProjectTooltip(true);
    if ($viewport.isMobile) {
      actionProject = project;
      actionsOpen = true;
      return;
    }
    openProjectRail(project.id);
  }

  let projectActions = $derived.by(() => {
    const project = actionProject;
    if (!project) return [];
    return [
      {
        label: 'Schedule…',
        icon: 'calendar' as const,
        onSelect: () => openProjectScheduleModal(project),
      },
      { label: 'Open project', icon: 'open' as const, onSelect: () => openProjectRail(project.id) },
    ];
  });
</script>

{#if $isWorkQueueOpen && $workQueueTab === 'backlog'}
  <aside
    class="backlog-rail flex w-72 shrink-0 flex-col border-l border-gray-200 bg-white"
    class:in-sheet={$viewport.isMobile}
    data-sheet-fill
  >
    <!-- Header -->
    <header class="border-b border-gray-200 bg-gray-50 px-2 py-2" data-sheet-drag-handle>
      <div class="mb-1 flex items-center justify-between">
        <h2 class="flex items-center gap-1.5 text-xs font-bold text-gray-800" data-sheet-title>
          <span>📥</span>
          Project backlog
        </h2>
        <div class="flex items-center gap-0.5">
          <button
            class="header-btn flex h-5 w-5 items-center justify-center rounded text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
            onclick={handleAddWithModal}
            title="Add new project"
          >
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
          <button
            class="flex h-5 w-5 items-center justify-center rounded text-[10px] text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            onclick={closeWorkQueue}
            title="Close work queue"
            aria-label="Close work queue"
            data-sheet-close
          >
            {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
          </button>
        </div>
      </div>
      <div class="mb-2 text-[10px] leading-[1.4] text-gray-500">
        Unscheduled projects ready to plan
      </div>
      <WorkQueueTabs backlogCount={projects.length} />
    </header>

    <!-- Search & Filter -->
    <div class="space-y-1.5 border-b border-gray-200 bg-gray-50 p-2">
      <!-- Search input -->
      <div class="relative">
        <svg
          class="absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          autocomplete="off"
          type="text"
          placeholder="Search..."
          class="w-full rounded border border-gray-300 py-1 pr-7 pl-7 text-xs
                 focus:border-transparent focus:ring-1 focus:ring-blue-400 focus:outline-none"
          bind:value={searchQuery}
        />
        {#if searchQuery}
          <button
            class="absolute top-1/2 right-1.5 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
            onclick={() => (searchQuery = '')}
            title="Clear search"
          >
            <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        {/if}
      </div>

      <!-- Category filter pills -->
      <div class="flex flex-wrap gap-0.5">
        <button
          class="category-pill rounded-full px-1.5 py-0.5 text-[10px] transition-colors"
          class:bg-gray-700={!selectedCategory}
          class:text-white={!selectedCategory}
          class:bg-gray-100={selectedCategory}
          class:text-gray-600={selectedCategory}
          class:hover:bg-gray-200={selectedCategory}
          onclick={() => (selectedCategory = null)}
          title="Show all categories"
        >
          All
        </button>
        {#each categories as cat (cat.value)}
          <button
            class="category-pill rounded-full px-1.5 py-0.5 text-[10px] transition-colors"
            class:bg-blue-500={selectedCategory === cat.value}
            class:text-white={selectedCategory === cat.value}
            class:bg-gray-100={selectedCategory !== cat.value}
            class:hover:bg-gray-200={selectedCategory !== cat.value}
            onclick={() => toggleCategory(cat.value)}
            title={cat.label}
          >
            {cat.icon}
          </button>
        {/each}
      </div>

      <!-- Filter status -->
      {#if isFiltered}
        <div class="flex items-center justify-between text-[10px] text-gray-500">
          <span>
            Showing {filteredProjects.length} of {projects.length}
          </span>
          <button class="text-blue-600 hover:text-blue-700 hover:underline" onclick={clearFilters}>
            Clear filters
          </button>
        </div>
      {/if}
    </div>

    <!-- Project list (drop zone) -->
    <div
      class="backlog-list custom-scrollbar flex-1 overflow-y-auto p-1.5 transition-colors"
      class:bg-blue-50={isDropTarget}
      class:ring-2={isDropTarget}
      class:ring-inset={isDropTarget}
      class:ring-blue-400={isDropTarget}
      ondragover={handleDragOver}
      ondragleave={handleDragLeave}
      ondrop={handleDrop}
      role="list"
      aria-label="Backlog projects"
    >
      {#if projects.length === 0}
        <div class="py-4 text-center text-gray-400">
          <p class="mb-1 text-2xl">📭</p>
          <p class="text-xs">No projects</p>
          <p class="mt-0.5 text-[10px]">Click + or drag here</p>
        </div>
      {:else if filteredProjects.length === 0}
        <div class="py-4 text-center text-gray-400">
          <p class="mb-1 text-xl">🔍</p>
          <p class="text-xs">No matches</p>
          <button class="mt-1 text-[10px] text-blue-600 hover:underline" onclick={clearFilters}>
            Clear filters
          </button>
        </div>
      {:else}
        <div class="flex flex-col gap-1">
          {#each filteredProjects as project (project.id)}
            {@const counts = project.taskCounts || { total: 0, done: 0, todo: 0 }}
            {@const hasTasks = counts.total > 0}
            {@const pct = hasTasks ? Math.round((counts.done / counts.total) * 100) : 0}
            {@const progressStyle = hasTasks && pct > 0 ? `--progress: ${pct}%;` : ''}
            {@const isComplete = hasTasks && counts.done === counts.total}
            <div
              class="backlog-chip {statusAccentColors[project.status]}"
              class:has-tasks={hasTasks}
              style={progressStyle}
              data-progress-complete={isComplete || undefined}
              role="button"
              tabindex="0"
              draggable={!$viewport.isMobile}
              ondragstart={(e) => {
                draggedProject.set(project);
                if (e.dataTransfer) {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(project.id));
                  e.dataTransfer.setData('application/project', String(project.id));
                }
              }}
              ondragend={() => draggedProject.set(null)}
              onclick={() => handleChipClick(project)}
              onkeydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChipClick(project);
                }
              }}
              onmouseenter={(e) => showProjectTooltip(project, e.currentTarget as HTMLElement)}
              onmouseleave={() => hideProjectTooltip()}
            >
              <span class="chip-category">{categoryIcons[project.category]}</span>
              {#if project.isPriority}
                <span class="chip-star">★</span>
              {/if}
              <span class="chip-title">{project.title}</span>
              {#if hasTasks}
                <span class="chip-task-count" class:all-done={isComplete}>
                  {counts.done}/{counts.total}
                </span>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Footer hint -->
    <footer
      class="backlog-footer border-t border-gray-200 bg-gray-50 px-2 py-1.5 text-[10px] text-gray-400"
    >
      {#if $viewport.isMobile}
        Tap a project to schedule it
      {:else}
        Click to edit • Drag to months →
      {/if}
    </footer>
  </aside>
{/if}

{#if $viewport.isMobile}
  <ActionSheet
    open={actionsOpen}
    onClose={() => (actionsOpen = false)}
    title={actionProject?.title}
    actions={projectActions}
  />
{/if}

<style>
  /* Mobile: inside the Dashboard's rail bottom sheet. */
  .backlog-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
  }

  .backlog-rail.in-sheet header {
    background: white;
  }

  .backlog-rail.in-sheet .backlog-list {
    min-height: 0;
    overscroll-behavior: contain;
  }

  .backlog-rail.in-sheet .header-btn {
    width: var(--mobile-tap);
    height: var(--mobile-tap);
    margin: -0.5rem 0;
    font-size: 0.875rem;
  }

  .backlog-rail.in-sheet .header-btn :global(svg) {
    width: 1.125rem;
    height: 1.125rem;
  }

  .backlog-rail.in-sheet input {
    min-height: var(--mobile-tap);
  }

  .backlog-rail.in-sheet .category-pill {
    min-width: var(--mobile-tap);
    min-height: var(--mobile-tap);
    font-size: 0.875rem;
  }

  .backlog-rail.in-sheet .backlog-chip {
    min-height: var(--mobile-tap);
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.875rem;
  }

  .backlog-rail.in-sheet .chip-category,
  .backlog-rail.in-sheet .chip-star {
    font-size: 0.875rem;
  }

  .backlog-rail.in-sheet .chip-task-count {
    font-size: 0.75rem;
  }

  .backlog-rail.in-sheet .backlog-footer {
    padding: 0.625rem 0.75rem;
    font-size: 0.8125rem;
    color: #6b7280;
  }

  .backlog-chip {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: pointer;
    transition: all 0.15s;
    min-width: 0;
    overflow: hidden;
    position: relative;
  }

  .backlog-chip:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .backlog-chip:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  /* Progress cross-hatch fill */
  .backlog-chip[style*='--progress']::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: repeating-linear-gradient(
      -45deg,
      var(--progress-color, rgba(0, 0, 0, 0.06)),
      var(--progress-color, rgba(0, 0, 0, 0.06)) 2px,
      transparent 2px,
      transparent 5px
    );
    clip-path: polygon(0 0, var(--progress, 0%) 0, calc(var(--progress, 0%) + 0.5rem) 100%, 0 100%);
  }

  .backlog-chip[data-progress-complete]::after {
    background: var(--progress-color, rgba(0, 0, 0, 0.06));
    clip-path: none;
  }

  /* Thin angled border at progress edge */
  .backlog-chip[style*='--progress']::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    clip-path: polygon(
      var(--progress, 0%) 0,
      calc(var(--progress, 0%) + 1px) 0,
      calc(var(--progress, 0%) + 0.5rem + 1px) 100%,
      calc(var(--progress, 0%) + 0.5rem) 100%
    );
    background: var(--chip-border-color, #e5e7eb);
    opacity: 0.45;
  }

  .backlog-chip[data-progress-complete]::before {
    display: none;
  }

  /* Content stays above progress fill */
  .backlog-chip > * {
    position: relative;
    z-index: 2;
  }

  .chip-category {
    font-size: 0.5625rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .chip-star {
    color: #facc15;
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2937;
    min-width: 0;
    flex: 1;
  }

  .chip-task-count {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  .chip-task-count.all-done {
    color: #16a34a;
  }

  /* Status accent colors */
  .backlog-chip.status-planned {
    --chip-border-color: #93c5fd;
  }

  .backlog-chip.status-active {
    --chip-border-color: #fcd34d;
  }

  .backlog-chip.status-almost {
    --chip-border-color: #fdba74;
  }

  .backlog-chip.status-done {
    --chip-border-color: #86efac;
  }
</style>
